import type {
    PatientDetailDto,
    SeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import type { InstanceAddress } from '~/backend/connector-next-api/dataRetrieval';
import { seriesKey } from '~/backend/connector-next-api/seriesKey';
import { Modality, strToModality } from '~/domain/dicom/modality';

/**
 * One addressable series of the selection, kept grouped so the open can make
 * a per-series decision (blocking download vs progressive streaming) that the
 * flat {@link OpenTargets.instances} list cannot express.
 */
export type ResolvedSeries = {
    /**
     * Connector database id of the series' dataset — the `{id}` of
     * `GET /api/images/{id}`. Absent when the detail did not carry one, which
     * rules the series out of the manifest-driven path.
     */
    datasetId: string | undefined;
    studyInstanceUid: string;
    seriesInstanceUid: string;
    /** Modality as the detail declares it; `UNKNOWN` when it declares none. */
    modality: Modality;
    /**
     * The connector's own verdict on whether the series forms a 3D volume.
     * `false` (never absent, which reads as "not stated") blocks the
     * progressive path: a manifest for a series the backend already doubts is
     * not something to build a volume from.
     */
    isValid3D: boolean;
    /** This series' SOP instances, in detail order. */
    instances: InstanceAddress[];
};

export type OpenTargets = {
    /** Every downloadable SOP instance, in study/series order. */
    instances: InstanceAddress[];
    /**
     * The same instances grouped by series, in the same order. Concatenating
     * `series[i].instances` reproduces {@link instances} exactly.
     */
    series: ResolvedSeries[];
    /**
     * Selected keys matching no series, plus considered series that cannot
     * be addressed on the wire; reported so a selection never loses a
     * dataset silently.
     */
    unresolved: number;
    /**
     * Selected series the source is still storing instances into. They are
     * left out of {@link instances} and {@link series} rather than retrieved.
     *
     * A push announces no total, so the detail lists only what has landed and
     * every one of those downloads cleanly — no shortfall, no dialog, nothing
     * on screen to say the series is short. That is true of ANY load: reading
     * it in one blocking download produces the same truncated volume as
     * streaming would, and with less to show for it. Excluding the series
     * here, where every load resolves its targets, refuses it for all of them
     * at one point (SR-WOR-H-37-3).
     */
    receiving: number;
    /**
     * Slices listed without a SOP Instance UID. Counted, not dropped: they
     * are missing files exactly like failed downloads, and reach the user
     * through the same shortfall confirmation.
     */
    unaddressableSlices: number;
};

/**
 * Resolves a patient detail tree to the SOP instances to open in the viewer.
 *
 * `seriesKeys` is the drilldown selection, or `'all'`. The download route is
 * keyed by DICOM UIDs, so the study UID is recovered by walking the tree. A
 * series contributes its per-slice SOP UIDs, or its single `sopInstanceUid`
 * for RT objects; one that cannot be addressed contributes nothing and
 * counts as unresolved, and one still being received contributes nothing and
 * counts as receiving.
 */
export function resolveOpenTargets(
    detail: PatientDetailDto | undefined,
    seriesKeys: readonly string[] | 'all'
): OpenTargets {
    const wanted = seriesKeys === 'all' ? null : new Set(seriesKeys);
    const consumed = new Set<string>();
    const instances: InstanceAddress[] = [];
    const resolvedSeries: ResolvedSeries[] = [];
    let unresolved = 0;
    let receiving = 0;
    let unaddressableSlices = 0;

    for (const study of detail?.studies ?? []) {
        const studyInstanceUid = study.studyInstanceUid;
        for (const series of study.series ?? []) {
            if (wanted) {
                const key = seriesKey(series);
                if (key === undefined || !wanted.has(key)) {
                    continue;
                }
                consumed.add(key);
            }
            const seriesInstanceUid = series.seriesInstanceUid;
            const { sopUids, dropped } = instanceSopUids(series);
            if (
                !studyInstanceUid ||
                !seriesInstanceUid ||
                sopUids.length === 0
            ) {
                // The whole series is unopenable, so its slices are already
                // covered by the dataset count and must not be counted twice.
                unresolved += 1;
                continue;
            }
            if (series.isReceiving === true) {
                receiving += 1;
                continue;
            }
            unaddressableSlices += dropped;
            const seriesInstances = sopUids.map((sopInstanceUid) => ({
                studyInstanceUid,
                seriesInstanceUid,
                sopInstanceUid,
            }));
            instances.push(...seriesInstances);
            resolvedSeries.push({
                datasetId: series.id,
                studyInstanceUid,
                seriesInstanceUid,
                modality: strToModality(series.modality ?? ''),
                isValid3D: series.isValid3D === true,
                instances: seriesInstances,
            });
        }
    }

    if (wanted) {
        for (const key of wanted) {
            if (!consumed.has(key)) {
                unresolved += 1;
            }
        }
    }

    return {
        instances,
        series: resolvedSeries,
        unresolved,
        receiving,
        unaddressableSlices,
    };
}

/**
 * The SOP instances of a series: an image contributes its slices, an RT
 * object its single SOP instance; `dropped` counts slices carrying no SOP
 * UID.
 */
function instanceSopUids(series: SeriesDto): {
    sopUids: string[];
    dropped: number;
} {
    const slices = series.slices ?? [];
    const sliceUids = slices
        .map((slice) => slice.sopInstanceUid)
        .filter((uid): uid is string => !!uid);
    if (sliceUids.length > 0) {
        return {
            sopUids: sliceUids,
            dropped: slices.length - sliceUids.length,
        };
    }
    return {
        sopUids: series.sopInstanceUid ? [series.sopInstanceUid] : [],
        dropped: 0,
    };
}
